import { stageContext } from 'src/globals';
import { type ELanguage, getLanguageTexts } from './Language/languageHelper';
import { LogoKeys } from './s3Keys';

export enum EWebsocketTopics
{
    TEST = 'test',
    analyseCvText = 'analyseCvText',
    analyseOpportunity = 'analyseOpportunity',
    generateSkillMetadata = 'generateSkillMetadata',
    generateCertificateMetadata = 'generateCertificateMetadata',
    generateCertificateDuration = 'generateCertificateDuration',
    generateRoleMetadata = 'generateRoleMetadata',
    generateCompetenceMetadata = 'generateCompetenceMetadata',
    suggestCategorizeSkills = 'suggestCategorizeSkills',
    suggestCategorizationForSkillsWebsocket = 'suggestCategorizationForSkillsWebsocket',
    optimizeDescription = 'optimizeDescription',
    refreshToken = 'refreshToken',
    INIT = 'INIT',
    attachmentUpload = 'attachmentUpload',
    NOTIFICATION = 'notification',
    SUGGESTIONS = 'suggestions',
    GENERAL = 'GENERAL',
    CHAT_AGENT = 'CHAT_AGENT',

    // EmployeeTalk Event-Driven Architecture (NEW)
    // Frontend → Backend: Send events (UPDATE_COMPONENT_DATA, NAVIGATE_TO_STEP, etc.)
    EMPLOYEE_TALK_EVENT = 'EMPLOYEE_TALK_EVENT',

    // EmployeeTalk State Updates (Backend → Frontend ONLY)
    // Backend sends validated state updates to all active EmployeeTalk clients
    // Frontend ONLY receives this message type, never sends it
    EMPLOYEE_TALK_STATE_UPDATE = 'EMPLOYEE_TALK_STATE_UPDATE',
}

export enum EEntityType
{
    ABSENCE = 'absence',
    AVAILABILITYMONTH = 'availmonth',
    BUSINESS = 'business',
    CERTIFICATE = 'certificate',
    COMPETENCE = 'competence',
    EMPLOYEE = 'employee',
    EMPLOYEE_MONTH_PROJECT_DATA = 'empmonthproject',
    EMPLOYEESETTINGS = 'empsettings',
    OPPORTUNITY = 'opportunity',
    ORGANISATIONAL_UNIT = 'orgunit',
    PRODUCT = 'product',
    PROJECT = 'project',
    ROLE = 'role',
    SKILL = 'skill',
    SKILL_CATEGORY = 'skillcategory',
    SMALL_CERTIFICATE = 'smallCertificate',
    SMALL_COMPETENCE = 'smallCompetence',
    SMALL_EMPLOYEE = 'smallEmployee',
    SMALL_ROLE = 'smallRole',
    SMALL_SKILL = 'smallSkill',
    ATTACHMENT = 'attachment',
    ADMIN_NOTIFICATION = 'adminNotification',
    NOTIFICATION = 'notification',
    EMPLOYEE_TALK = 'empTalk',
    EMPLOYEE_TALK_TEMPLATE = 'ett',
    DASHBOARD = 'dashboard',
}

export enum EMailCycle
{
    NEVER = 'never',
    MONTLY = 'monthly',
}

//Falls jemals ein Update gemacht wird auch im SkillMetaDataGenerator berücksichtigen
export enum ESkillType
{
    HARD_SKILL = 'Hard-Skill',
    SOFT_SKILL = 'Soft-Skill',
    LANGUAGE_SKILL = 'Sprach-Skill',
}

/**
 * Converts an entity type to string representation.
 *
 * @param {ELanguage} lang - The language to use for the conversion.
 * @param {EEntityType} type - The entity type to convert.
 * @returns {string} The converted entity type as a string.
 */
export function entityTypeToString(lang: ELanguage, type: EEntityType): string
{
    const langStrings = getLanguageTexts(lang).entityTypes;
    return langStrings[type];
}
export enum EDashboard
{
    EMPLOYEE = 'employee',
    MANAGER = 'manager',
    ADMIN = 'admin',
    HR = 'hr',
}
export enum ELinks
{
    LOGIN = 'login',
    LOGGEDIN = 'loggedin',
    LOGGEDOUT = 'loggedout',
    CERTIFICATES = 'certificates',
    COMPETENCE = 'competence',
    EMPLOYEE_TALKS = 'employee-talks',
    PEOPLE = 'people-org',
    PEOPLE_EMPLOYEES = 'people-org/employee',
    PEOPLE_ORGUNITS = 'people-org/orgunits',
    PROFILE = 'profile',
    PROFILE_EDIT = 'profile/edit',
    PROFILE_EDIT_ANALYSE = 'profile/edit/analyse',
    PROFILE_EDIT_ROLES = 'profile/edit/roles',
    PROFILE_EDIT_CERTIFICATES = 'profile/edit/certificates',
    PROFILE_EDIT_DEVELOPMENT = 'profile/edit/development',
    PROFILE_EDIT_DEVELOPMENT_NEW = 'profile/edit/development-new',
    PROFILE_EDIT_FORECAST = 'profile/edit/forecast',
    PROFILE_EDIT_SKILLS = 'profile/edit/skills',
    PROFILE_EDIT_SKILLS_SUGGESTIONS = 'profile/edit/skills/suggestions',

    DASHBOARD = 'dashboard',
    DASHBOARD_EMPLOYEE = `dashboard/employee`,
    DASHBOARD_MANAGER = `dashboard/manager`,
    DASHBOARD_HR = `dashboard/hr`,
    DASHBOARD_ADMIN = `dashboard/admin`,

    PROFILE_SHOW = 'profile/show',
    PUBLIC_PROFILE = 'p', // For shareable URLs: /p/{slug}
    SALES = 'sales',
    SALES_PRODUCTS = 'sales/products',
    SALES_CUSTOMERS = 'sales/customers',
    SALES_OPPORTUNITIES = 'sales/opportunities',
    SALES_PROJECTS = 'sales/projects',
    SETTINGS = 'settings',
    SETTINGS_CALCULATIONS = 'settings/calculations',
    SETTINGS_EMAIL = 'settings/mails',
    SETTINGS_EMPLOYEE_TALK_TEMPLATE = 'settings/employeetalk',
    SETTINGS_TIME_SYNC = 'settings/timesync',
    SETTINGS_HOLIDAYS = 'settings/holidays',
    SETTINGS_LEVELS = 'settings/levels',
    SETTINGS_ORGUNITS = 'settings/orgunits',
    SETTINGS_PERMISSIONS = 'settings/permissions',
    SETTINGS_REGISTRATION = 'settings/registration',
    SETTINGS_STYLE = 'settings/style',
    SETTINGS_RESTAPI = 'settings/restapi',
    SETTINGS_MICROSOFT = 'settings/microsoft',
    SETTINGS_SUGGESTIONS = 'settings/suggestions',
    SETTINGS_SLACK = 'settings/slack',
    SKILLCATEGORIES = 'skillcategories',
    SKILLS = 'skills',
    ROLES = 'roles',
    DOCUMENTATION = 'documentation',
    DOCUMENTATION_REST = 'documentation/restapi',
    EMAIL_UNSUBSCRIBE = 'unsubscribe',
    EMAIL_SETTINGS = 'email-settings',
    MESSAGES = 'messages',
    MY_TEAM = 'my-team',
    MY_TEAM_MANAGE = 'my-team/manage',
    COMPANY = 'unternehmen',
    NAVIGATOR = 'navigator',
    ONBOARDING = 'onboarding',
    COMPANY_REGISTRATION = 'company-registration',
    IMPRESSUM = 'impressum',
    KONTAKT = 'kontakt',
    DATENSCHUTZ = 'datenschutz',

    FOOTER_IMPRINT = 'https://t4lo.de/impressum',
    BUGREPORT = 'mailto:inbox@talentillion.de',
}

/**
 * Converts the entity type and id to a link.
 *
 * @param {number} id - The id of the entity.
 * @param {EEntityType} type - The type of the entity.
 * @returns {string} The link generated from the entity type and id.
 */
/**
 * Generates a link to a public profile by slug
 * @param slug The URL slug for the profile
 * @returns Public profile link
 */
export function publicProfileLink(slug: string): string
{
    return `/${ELinks.PUBLIC_PROFILE}/${slug}`;
}

export function entityTypeToLink(id: number | string, type: EEntityType, tab?: string, itsMe?: boolean): string
{
    let link = '';
    switch (type)
    {
        case EEntityType.SKILL_CATEGORY:
            link = `/${ELinks.SKILLCATEGORIES}/${id}`;
            break;
        case EEntityType.PRODUCT:
            link = `/${ELinks.SALES_PRODUCTS}/${id}`;
            break;
        case EEntityType.AVAILABILITYMONTH:
            link = `/${ELinks.PROFILE_EDIT_FORECAST}`;
            break;
        case EEntityType.EMPLOYEE:
            if (itsMe) link = `/${ELinks.PROFILE_SHOW}`;
            else link = `/${ELinks.PEOPLE_EMPLOYEES}/${id}`;
            break;
        case EEntityType.SKILL:
            link = `/${ELinks.SKILLS}/${id}`;
            break;
        case EEntityType.CERTIFICATE:
            link = `/${ELinks.CERTIFICATES}/${id}`;
            break;
        case EEntityType.PROJECT:
            link = `/${ELinks.SALES_PROJECTS}/${id}`;
            break;
        case EEntityType.BUSINESS:
            link = `/${ELinks.SALES_CUSTOMERS}/${id}`;
            break;
        case EEntityType.COMPETENCE:
            link = `/${ELinks.COMPETENCE}/${id}`;
            break;
        case EEntityType.OPPORTUNITY:
            link = `/${ELinks.SALES_OPPORTUNITIES}/${id}`;
            break;
        case EEntityType.ROLE:
            link = `/${ELinks.ROLES}/${id}`;
            break;
        case EEntityType.ORGANISATIONAL_UNIT:
            link = `/${ELinks.PEOPLE_ORGUNITS}/${id}`;
            break;
        case EEntityType.EMPLOYEE_TALK_TEMPLATE:
            link = `/${ELinks.SETTINGS_EMPLOYEE_TALK_TEMPLATE}/${id}`;
            break;
        case EEntityType.EMPLOYEE_TALK:
            link = `/${ELinks.EMPLOYEE_TALKS}/${id}`;
            break;
        default:
            return '/unknown-entity';
    }
    if (!tab) return link;
    return `${link}/${tab}`;
}

/**
 * Checks if the given argument is of type ESkillType.
 *
 * @param {ESkillType} arg - The argument to check.
 * @return {boolean} - Returns true if the argument is of type ESkillType, otherwise false.
 */
export function isESkillType(arg: ESkillType): arg is ESkillType
{
    return Object.values(ESkillType).includes(arg);
}

/**
 * Sensibilität für EmployeeSugestionBerechnung
 */
export const sensitivityEmployeeSuggestionCalculation = {
    missingSkill: -0.1,
    lowLevel: -0.05,
    highLevel: 0.01,
};

export enum EEmployeePictureSize
{
    XS = 38,
    S = 100,
    M = 300,
    L = 500,
    XL = 1000,
}

export function generateEmployeePictureUrl(
    companyId: string,
    employee: { id: number; pictureId: string | null },
    size: EEmployeePictureSize,
)
{
    return `https://${(stageContext as { domain?: string }).domain}/static/profilepictures/${companyId}/${employee.id}-${size}-${employee.pictureId}.png`;
}

/**
 * Backend-compatible function that generates company logo URL with direct parameters
 * Can be used in both frontend and backend contexts
 */
export function generateCompanyLogoUrlDirect(
    companyId: string | null,
    logoIdLight: string | null,
    logoIdDark: string | null,
    size: number = 100,
    isDarkMode: boolean = false,
): string
{
    // Select appropriate logo
    const logoId = isDarkMode ? logoIdDark || logoIdLight : logoIdLight;

    if (!logoId || !companyId)
    {
        // Fallback to T4LO logo based on theme
        return isDarkMode ? '/t4lo-logo-app-dark.png' : '/t4lo-logo-app-light.png';
    }

    return `https://${(stageContext as { domain?: string }).domain}/${LogoKeys.static(companyId, size, logoId)}`;
}

export enum EShareBy
{
    mail = 'email',
    teams = 'teams',
    slack = 'slack',
}

/**
 * Maximum number of SuperPowers an employee can have
 */
export const MAX_SUPER_POWERS = 6;

/**
 * Generates a public profile URL for sharing
 * @param employee Employee data with firstName, lastName, and optional customUrlSlug
 * @returns Shareable profile URL
 */
export function generatePublicProfileUrl(employee: {
    firstName: string;
    lastName: string;
    customUrlSlug?: string;
}): string
{
    const baseUrl = `${(stageContext as { domain?: string }).domain || 't4lo.de'}/p/`;

    if (employee.customUrlSlug)
    {
        return baseUrl + employee.customUrlSlug;
    }

    // Fallback: firstname-lastname
    const slug = `${employee.firstName}-${employee.lastName}`.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    return baseUrl + slug;
}

/**
 * Validates a custom URL slug
 * @param slug The slug to validate
 * @returns true if valid, false otherwise
 */
export function validateUrlSlug(slug: string): boolean
{
    // 3-50 characters, alphanumeric + hyphens only, no consecutive hyphens
    const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    const reservedWords = ['admin', 'api', 'www', 'app', 'mail', 'ftp', 'blog', 'shop', 'store', 'help', 'support'];

    return slug.length >= 3 && slug.length <= 50 && slugRegex.test(slug) && !reservedWords.includes(slug);
}

export const CONST_AVAILABILITY_DAYS = 28;
export const CONST_MS_ONE_DAY = 24 * 60 * 60 * 1000;
export const CONST_WORK_HOURS_PER_DAY = 8;

/**
 * Formats a phone number for display
 * Handles numbers starting with 0 or +XX
 * @param phoneNumber Raw phone number string
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string
{
    if (!phoneNumber) return '';

    const cleaned = phoneNumber.replace(/\s+/g, '');

    // International format (+XX...)
    if (cleaned.startsWith('+'))
    {
        const countryCode = cleaned.slice(0, 3);
        const rest = cleaned.slice(3);
        return `${countryCode} ${rest.replace(/(\d{3})(?=\d)/g, '$1 ')}`;
    }

    // National format (0...)
    if (cleaned.startsWith('0'))
    {
        return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    }

    return phoneNumber;
}
